import { useState, useMemo } from "react";
import PageHeader from "@/components/PageHeader";
import { Helmet } from "react-helmet-async";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Wrench, Zap, Wind, HardHat, Calculator, ClipboardList, Settings, Star, Search, Sparkles, Keyboard, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { APP_VERSION_LABEL } from "@/lib/version";
import { shortcutGroups } from "@/components/KeyboardShortcuts";
import OfflineChecklist from "@/components/OfflineChecklist";

const changelogItems = [
  {
    version: APP_VERSION_LABEL,
    date: import.meta.env.VITE_BUILD_DATE || "Latest",
    changes: [
      "Auto CalVer versioning — version now updates with every build",
      "Consolidated Formulas into each trade page (Electrical, Pipe, Ductwork, Structural)",
      "Inline unit converter on the Home dashboard",
      "Merged Flanges/Gaskets/Face-Finish into single Pipe Fittings tab",
      "Combined Flange Torque & Bolt Torque into unified Torque tool",
      "Ductwork categories streamlined from 5 to 4",
      "Expanded search index with 80+ tools and natural-language Problem Solver",
    ],
  },
  {
    version: "v1.2.0",
    date: "Mar 2026",
    changes: [
      "Help & FAQ page with Getting Started walkthrough",
      "Hazen-Williams C-value badges on pipe material settings",
      "What's New changelog section",
      "Keyboard shortcuts overlay",
    ],
  },
  {
    version: "v1.1.0",
    date: "Feb 2026",
    changes: [
      "Structural tools — beam spans, anchor pull-out, seismic bracing",
      "Job Log with PDF export",
      "Drag-to-reorder favorites",
      "Swipe navigation between tabs",
    ],
  },
  {
    version: "v1.0.0",
    date: "Jan 2026",
    changes: [
      "Pipe sizing with Hazen-Williams friction loss",
      "NEC wire sizing, conduit fill, voltage drop",
      "Duct sizing with SMACNA leakage classes",
      "Unit converter and formula reference cards",
      "PWA offline support and install prompt",
    ],
  },
];

const faqItems = [
  {
    q: "How do I save favorite tools?",
    a: "On the Home screen, tap the ★ star icon on any tool card to add it to your Favorites row at the top. Long-press and drag to reorder favorites.",
  },
  {
    q: "Where is the unit converter?",
    a: "The unit converter is built right into the Home dashboard — scroll down to find it. Convert length, pressure, temperature, and more without leaving the main screen.",
  },
  {
    q: "Where are the formulas?",
    a: "Formulas are now integrated into each trade page. Open Electrical, Pipe, Ductwork, or Structural and look for the Formulas tab — you'll find the relevant formulas right where you need them.",
  },
  {
    q: "Do my settings sync across devices?",
    a: "Settings are saved locally on each device using your browser's storage. They don't sync between devices, so you'll need to set defaults on each one.",
  },
  {
    q: "Can I use this app offline?",
    a: "Yes! After your first visit, the app is cached as a PWA. All calculators and reference data work offline. An indicator appears if you lose connection.",
  },
  {
    q: "How do I install it on my phone?",
    a: "On iOS Safari, tap the Share button → 'Add to Home Screen.' On Android Chrome, tap the three-dot menu → 'Install app.' You can also visit the Install page from the More menu.",
  },
  {
    q: "What codes and standards are the calculations based on?",
    a: "Pipe sizing uses Hazen-Williams. Electrical wire sizing follows NEC Table 310.16 ampacity data. Duct sizing follows SMACNA and ASHRAE friction-rate methods. Structural tools reference IBC/AISC for beam and anchor data. Always verify with local code authority.",
  },
  {
    q: "How accurate are the results?",
    a: "Results are engineering-grade estimates suitable for field work and preliminary design. For stamped drawings or permit submittals, verify with a licensed engineer and your local jurisdiction.",
  },
  {
    q: "How do I change default units or materials?",
    a: "Go to Settings (gear icon in the More menu). You can set default conductor material, voltage, pipe material, duct class, and unit system (Imperial/Metric).",
  },
  {
    q: "What is the Job Log for?",
    a: "The Job Log lets you track daily hours, notes, and job details on-site. You can export entries as a PDF for record-keeping or submitting to your employer.",
  },
];

const toolGuides = [
  {
    icon: Wrench,
    title: "Pipe Sizing",
    desc: "Size water, hydronic, and process piping by flow rate or fixture units. Includes Hazen-Williams friction loss, GPM-to-velocity, plumbing DFU/WSFU lookup, and consolidated flange/gasket/torque references in a single Fittings tab.",
    tips: ["Set your default pipe material in Settings for faster lookups", "Flanges, gaskets, and face/finish are now combined under Fittings → Flanges", "Trade formulas are built into the Formulas tab on this page"],
  },
  {
    icon: Zap,
    title: "Electrical",
    desc: "NEC-based wire sizing, conduit fill, voltage drop, motor FLA, service entrance sizing, ampacity derating, and panel schedule estimator. Includes built-in electrical formulas and Ohm's law reference.",
    tips: ["Set default voltage, phases, and conductor material in Settings", "Conduit fill tool auto-checks 40% fill per NEC 344.22", "Find formulas under the Formulas tab — no separate page needed"],
  },
  {
    icon: Wind,
    title: "Ductwork",
    desc: "Equal-friction duct sizing, round/rectangular equivalents, duct leakage by SMACNA seal class, refrigerant line sizing, and room CFM calculators — all streamlined into four categories: Sizing, Pressure, Properties, and Refrigerant.",
    tips: ["Choose your SMACNA seal class in Settings for leakage calcs", "Room CFM and capacity tools are now under Properties", "Mechanical formulas are in the Formulas tab"],
  },
  {
    icon: HardHat,
    title: "Structural",
    desc: "Steel beam span tables, concrete anchor pull-out, seismic bracing loads, and hanger rod sizing. Reference data for common W-shapes and angle iron, plus built-in general formulas.",
    tips: ["Check seismic category before sizing bracing", "Anchor data is for cast-in-place — verify for post-installed anchors"],
  },
  {
    icon: Calculator,
    title: "Calculators",
    desc: "General-purpose trade calculators including BTU load, flow rate, pipe velocity, Ohm's law, and percentage helpers.",
    tips: ["BTU calculator covers both heating and cooling loads", "Ohm's law tool shows all four values from any two inputs"],
  },
  {
    icon: ClipboardList,
    title: "Job Log",
    desc: "Track daily work hours, job names, and field notes. Entries are saved locally and can be exported to PDF for payroll or documentation.",
    tips: ["Add notes as you go — they export with the PDF", "Use consistent job names for easy filtering"],
  },
];

const gettingStartedSteps = [
  { icon: Search, label: "Find a tool", detail: "Use the Home screen to browse categories or tap the search bar to jump straight to any calculator." },
  { icon: Star, label: "Save favorites", detail: "Tap the ★ icon on any tool card to pin it to your Favorites row. Long-press and drag to reorder." },
  { icon: Settings, label: "Set defaults", detail: "Head to Settings to choose your go-to voltage, pipe material, duct class, and unit system so every tool starts ready." },
  { icon: Calculator, label: "Run a calc", detail: "Open any tool, enter your values, and get instant results. Tap info icons for field-level guidance." },
  { icon: ClipboardList, label: "Log your work", detail: "Use the Job Log to track daily hours and notes on-site, then export to PDF when you need records." },
];

const highlightMatch = (text: string, term: string) => {
  if (!term) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  if (parts.length === 1) return text;
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === term ? (
          <mark key={i} className="bg-primary/20 text-foreground rounded-sm px-0.5">{part}</mark>
        ) : part
      )}
    </>
  );
};

const Help = () => {
  const [query, setQuery] = useState("");
  const [openFaqItems, setOpenFaqItems] = useState<string[]>([]);
  const q = query.toLowerCase().trim();

  const filteredFaq = useMemo(() =>
    q ? faqItems.filter(f => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)) : faqItems,
    [q]
  );

  const filteredGuides = useMemo(() =>
    q ? toolGuides.filter(g => g.title.toLowerCase().includes(q) || g.desc.toLowerCase().includes(q) || g.tips.some(t => t.toLowerCase().includes(q))) : toolGuides,
    [q]
  );

  const filteredChangelog = useMemo(() =>
    q ? changelogItems.filter(r => r.changes.some(c => c.toLowerCase().includes(q)) || r.version.toLowerCase().includes(q)) : changelogItems,
    [q]
  );

  const noResults = q && filteredFaq.length === 0 && filteredGuides.length === 0 && filteredChangelog.length === 0;

  return (
    <div className="pb-24 animate-fade-in">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqItems.map(item => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          })}
        </script>
      </Helmet>
      <PageHeader title="📖 Help & FAQ" subtitle="How to use Trades Toolbelt" />

      <div className="px-4 mt-4 space-y-8 max-w-lg mx-auto">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search help, FAQ, guides…"
            className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-secondary border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
          {query && (
            <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {noResults && (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">No results for "{query}"</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Try a different keyword</p>
          </div>
        )}

        {/* Getting Started — only when not filtering */}
        {!q && (
          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Getting Started</h2>
            <div className="trade-card space-y-0 divide-y divide-border">
              {gettingStartedSteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 shrink-0 mt-0.5">
                    <step.icon className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{step.label}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* iOS install / offline verification — only when not filtering */}
        {!q && <OfflineChecklist />}

        {/* Keyboard Shortcuts — only when not filtering */}
        {!q && (
          <section className="space-y-3" aria-labelledby="shortcuts-heading">
            <h2 id="shortcuts-heading" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Keyboard className="w-4 h-4" />
              Keyboard Shortcuts
            </h2>
            <div className="trade-card space-y-4">
              <p className="text-xs text-muted-foreground">
                Press <kbd className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded bg-secondary border border-border text-[10px] font-mono font-semibold text-foreground shadow-[0_1px_0_1px_hsl(var(--border))]">?</kbd> anytime to see the full shortcuts overlay.
              </p>
              {shortcutGroups.map((group) => (
                <div key={group.title}>
                  <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group.title}</h2>
                  <div className="space-y-1">
                    {group.shortcuts.map((shortcut, i) => (
                      <div key={i} className="flex items-center justify-between py-1">
                        <span className="text-sm text-foreground">{shortcut.desc}</span>
                        <div className="flex items-center gap-1 shrink-0 ml-3">
                          {shortcut.keys.map((key, j) => (
                            <span key={j} className="flex items-center gap-0.5">
                              <kbd className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded bg-secondary border border-border text-[10px] font-mono font-semibold text-foreground shadow-[0_1px_0_1px_hsl(var(--border))]">{key}</kbd>
                              {j < shortcut.keys.length - 1 && <span className="text-[10px] text-muted-foreground">+</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* What's New */}
        {filteredChangelog.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">What's New</h2>
            <div className="trade-card space-y-4">
              {filteredChangelog.map((release, i) => (
                <div key={i} className={i < filteredChangelog.length - 1 ? "pb-4 border-b border-border" : ""}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">{release.version}</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                      {release.date}
                    </Badge>
                  </div>
                  <ul className="space-y-1 ml-6">
                    {release.changes.map((change, j) => (
                      <li key={j} className="text-xs text-muted-foreground list-disc">{change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {filteredFaq.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Frequently Asked Questions {q && <span className="text-muted-foreground/60">({filteredFaq.length})</span>}
            </h2>
            <div className="trade-card">
              <Accordion type="multiple" className="w-full" value={q ? filteredFaq.map((_, i) => `faq-${i}`) : openFaqItems} onValueChange={v => !q && setOpenFaqItems(v)}>
                {filteredFaq.map((item, i) => (
                  <AccordionItem key={item.q} value={`faq-${i}`} className={i === filteredFaq.length - 1 ? "border-b-0" : ""}>
                    <AccordionTrigger className="text-sm text-left font-medium hover:no-underline">
                      {highlightMatch(item.q, q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {highlightMatch(item.a, q)}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {/* Tool Guides */}
        {filteredGuides.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Tool Guides {q && <span className="text-muted-foreground/60">({filteredGuides.length})</span>}
            </h2>
            <div className="space-y-3">
              {filteredGuides.map((tool, i) => (
                <div key={i} className="trade-card space-y-2">
                  <div className="flex items-center gap-2">
                    <tool.icon className="w-5 h-5 text-primary shrink-0" />
                    <h2 className="text-sm font-semibold text-foreground">{highlightMatch(tool.title, q)}</h2>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{highlightMatch(tool.desc, q)}</p>
                  {tool.tips.length > 0 && (
                    <ul className="space-y-1 pt-1">
                      {tool.tips.map((tip, j) => (
                        <li key={j} className="text-xs text-muted-foreground flex items-start gap-1.5">
                          <span className="text-primary mt-0.5">💡</span>
                          <span>{highlightMatch(tip, q)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-center text-[10px] text-muted-foreground/50 pb-4">
          Trades Toolbelt — Built for the field
        </p>
      </div>
    </div>
  );
};

export default Help;
